import { RESULT_STATUS } from "./result";
import { isModuleRegistered } from "SharedStores/util";
import { PROGRAM_NAMESPACE, PROGRAM_TYPES } from "SharedStores/Program/programStore";
import { PROJECT_TYPES, PROJECT_NAMESPACE } from "SharedStores/Project/projectStore";
import { HelperEvaluator } from "./helper";
import { DATA_FIELD_TYPE } from "Types/dataFieldTypes";
import { isValidDate } from "Validation/validator";
import { EXPRESSION_PROPERTY_NAMES } from "Formula/metadata.js";
import { DATA_FIELD_GROUP_TYPE } from "Types/dataFieldTypes.js";
import { convertToFiniteNumber, isFiniteNumber } from "Util/util.js";
import {
    ENERGY_STORAGE_SUBSYSTEM_TYPE,
    ENERGY_STORAGE_SUBSYSTEM_NAME,
    SUFFIXES as ESSSUFFIXES
} from "Forms/Components/ProjectForm/ProjectElements/energyStorageSystemElementController";
import { SUFFIXES as PVSUFFIXES } from "Forms/Components/ProjectForm/ProjectElements/pvSystemElementController";
import { COMPONENT_TYPE } from "Types/componentTypes";

export const BOOLEAN_VALUE = {
    false: 0,
    true: 1
};

export class MultiInstanceEvaluator extends HelperEvaluator {
    constructor(formulaEvaluator, storeContext, $serviceControllers) {
        super(formulaEvaluator, storeContext, $serviceControllers);
    }

    evaluateExistsSameMultiInstanceValue(exp) {
        if (!isModuleRegistered(PROGRAM_NAMESPACE, this.state)) {
            throw Error("program store not registered");
        }

        if (isModuleRegistered(PROJECT_NAMESPACE, this.state)) {
            const dataFieldGroupType = Number(
                exp.PropertyDictionary[EXPRESSION_PROPERTY_NAMES.MultiInstanceFieldGroupType]
            );
            const multiInstanceValues = this.getMultiValuesAsStrings(exp, dataFieldGroupType);
            return { Value: this.existsSameMultiInstanceValue(multiInstanceValues, dataFieldGroupType) };
        }

        exp.inError = true;
        exp.errorMessage = "Project context not found to evaluate data field reference.";
        return { Status: RESULT_STATUS.Error, Message: exp.errorMessage };
    }

    async evaluateCoalesceMultiInstanceValue($app, exp, resultType) {
        if (!isModuleRegistered(PROGRAM_NAMESPACE, this.state)) {
            throw Error("program store not registered");
        }

        if (isModuleRegistered(PROJECT_NAMESPACE, this.state)) {
            const dataFieldGroupType = Number(
                exp.PropertyDictionary[EXPRESSION_PROPERTY_NAMES.MultiInstanceFieldGroupType]
            );
            const multiInstanceValues = this.getMultiValuesAsStrings(exp, dataFieldGroupType);
            let resultVal = this.existsSameMultiInstanceValue(multiInstanceValues, dataFieldGroupType)
                ? String(multiInstanceValues[0])
                : null;

            if (resultVal !== null) {
                const currentField = this.getters[PROGRAM_TYPES.getters.dataFieldDefinitionFromId](
                    exp.PublicDataFieldDefinitionId
                );
                resultVal = await this.mapMultiInstanceValue(dataFieldGroupType, currentField.Name, resultVal, exp);
            }

            switch (resultType) {
                case DATA_FIELD_TYPE.DateTime:
                    if (resultVal == null || !isValidDate($app, resultVal)) {
                        const dfd = this.getters[PROGRAM_TYPES.getters.dataFieldDefinitionFromId](
                            exp.PublicDataFieldDefinitionId
                        );
                        exp.inError = true;
                        exp.errorMessage =
                            resultVal == null
                                ? `No value for datafield ${dfd.Name}.`
                                : `Value of data field ${dfd.Name} is not a valid date.`;
                        return { Status: RESULT_STATUS.Error, Message: exp.errorMessage };
                    }
                    return { Value: resultVal };
                case DATA_FIELD_TYPE.Decimal:
                    return { Value: Number(resultVal), Status: RESULT_STATUS.Success };
                case DATA_FIELD_TYPE.Boolean:
                    return { Value: Boolean(resultVal), Status: RESULT_STATUS.Success };
                default:
                    return { Value: resultVal };
            }
        }

        exp.inError = true;
        exp.errorMessage = "Project context not found to evaluate data field reference.";
        return { Status: RESULT_STATUS.Error, Message: exp.errorMessage };
    }

    evaluateSumMultiInstanceValue(exp) {
        if (!isModuleRegistered(PROGRAM_NAMESPACE, this.state)) {
            throw Error("program store not registered");
        }

        if (isModuleRegistered(PROJECT_NAMESPACE, this.state)) {
            const dataFieldGroupType = Number(
                exp.PropertyDictionary[EXPRESSION_PROPERTY_NAMES.MultiInstanceFieldGroupType]
            );
            const multiInstanceValues = this.getMultiValuesAsStrings(exp, dataFieldGroupType) || [];
            let sum = 0;
            for (const val of multiInstanceValues) {
                if (!this.isEmptyPVSystemOrEnergyStorage(val, dataFieldGroupType)) {
                    const num = convertToFiniteNumber(val);
                    if (isFiniteNumber(num)) {
                        sum += num;
                    }
                }
            }
            return { Value: sum };
        }

        exp.inError = true;
        exp.errorMessage = "Project context not found to evaluate data field reference.";
        return { Status: RESULT_STATUS.Error, Message: exp.errorMessage };
    }

    evaluateCountTrueMultiInstanceValue(exp) {
        if (!isModuleRegistered(PROGRAM_NAMESPACE, this.state)) {
            throw Error("program store not registered");
        }

        if (isModuleRegistered(PROJECT_NAMESPACE, this.state)) {
            const dataFieldGroupType = Number(
                exp.PropertyDictionary[EXPRESSION_PROPERTY_NAMES.MultiInstanceFieldGroupType]
            );
            const multiInstanceValues = this.getMultiValuesAsStrings(exp, dataFieldGroupType) || [];
            let count = 0;
            for (const multiInstanceValue of multiInstanceValues) {
                const val = multiInstanceValue || "";
                if (val.toLowerCase() === "true") {
                    count += 1;
                }
            }
            return { Value: count };
        }

        exp.inError = true;
        exp.errorMessage = "Project context not found to evaluate data field reference.";
        return { Status: RESULT_STATUS.Error, Message: exp.errorMessage };
    }

    async evaluateCountMatchingTextMultiInstanceValue(exp) {
        if (!isModuleRegistered(PROGRAM_NAMESPACE, this.state)) {
            throw Error("program store not registered");
        }

        if (!isModuleRegistered(PROJECT_NAMESPACE, this.state)) {
            exp.inError = true;
            exp.errorMessage = "Project context not found to evaluate data field reference.";
            return { Status: RESULT_STATUS.Error, Message: exp.errorMessage };
        }

        const textToMatch = exp.StringConstantValue || "";
        const dataFieldGroupType = Number(
            exp.PropertyDictionary[EXPRESSION_PROPERTY_NAMES.MultiInstanceFieldGroupType]
        );
        const multiInstanceValues = this.getMultiValuesAsStrings(exp, dataFieldGroupType) || [];

        const currentField = this.getters[PROGRAM_TYPES.getters.dataFieldDefinitionFromId](
            exp.PublicDataFieldDefinitionId
        );
        const mappedValues = await this.mapMultiInstanceValues(
            dataFieldGroupType,
            currentField.Name,
            multiInstanceValues,
            exp
        );

        let count = 0;
        for (const value of mappedValues) {
            const val = value || "";
            if (val === textToMatch) {
                count += 1;
            }
        }
        return { Value: count };
    }

    isEmptyPVSystemOrEnergyStorage(val, dataFieldGroupType) {
        return (
            val == "-1" &&
            (dataFieldGroupType === DATA_FIELD_GROUP_TYPE.EnergyStorageSystem ||
                dataFieldGroupType === DATA_FIELD_GROUP_TYPE.PVSystem)
        );
    }
    existsSameMultiInstanceValue(multiInstanceValues, dataFieldGroupType) {
        const first = multiInstanceValues && multiInstanceValues.length > 0 ? multiInstanceValues[0] : null;
        // For PV System and Energy Storage System -1 value represents empty element
        if (this.isEmptyPVSystemOrEnergyStorage(first, dataFieldGroupType)) {
            return false;
        }

        return first !== null && first !== "" && multiInstanceValues.every((val) => val === first);
    }

    getMultiValuesAsStrings(exp, dataFieldGroupType) {
        const multiInstanceValues = this.getters[PROJECT_TYPES.getters.dataFieldMultiValueFromId](
            exp.PublicDataFieldDefinitionId
        );

        if (dataFieldGroupType === DATA_FIELD_GROUP_TYPE.EnergyStorageSystem) {
            const selectedESSSubsystemType = Number(exp.PropertyDictionary[EXPRESSION_PROPERTY_NAMES.ESSSubsystemType]);
            if (selectedESSSubsystemType !== ENERGY_STORAGE_SUBSYSTEM_TYPE.Undefined) {
                const currentField = this.getters[PROGRAM_TYPES.getters.dataFieldDefinitionFromId](
                    exp.PublicDataFieldDefinitionId
                );
                if (this.isESSSubSystemField(currentField.Name)) {
                    const dfdList = this.getters[PROGRAM_TYPES.getters.dataFieldDefinitionsFromGroupId](
                        currentField.DataFieldGroupId
                    );
                    const essSubsystemTypeField = dfdList.find((x) =>
                        x.Name.endsWith(ESSSUFFIXES.inputFields.SubsystemType)
                    );

                    const essSubsystemTypeValues = this.getters[PROJECT_TYPES.getters.dataFieldMultiValueFromId](
                        essSubsystemTypeField.PublicId
                    );

                    const essSubsystemValues = [];

                    for (let i = 0; i < essSubsystemTypeValues.length; i++) {
                        if (Number(essSubsystemTypeValues[i]) === selectedESSSubsystemType) {
                            essSubsystemValues.push(multiInstanceValues[i]);
                        }
                    }
                    return essSubsystemValues;
                }
            }
        }
        return multiInstanceValues;
    }

    async mapMultiInstanceValues(dataFieldGroupType, fieldName, values, exp) {
        const mappedValues = [];
        for (const value of values) {
            if (value) {
                const mappedValue = await this.mapMultiInstanceValue(dataFieldGroupType, fieldName, value, exp);
                mappedValues.push(mappedValue);
            } else {
                mappedValues.push("");
            }
        }
        return mappedValues;
    }

    async mapMultiInstanceValue(dataFieldGroupType, fieldName, value, exp) {
        if (dataFieldGroupType == DATA_FIELD_GROUP_TYPE.PVSystem && value !== null) {
            if (fieldName.endsWith(PVSUFFIXES.inputFields.InverterManufacturer)) {
                return this.getManufacturerName(PVSUFFIXES.inputFields.InverterManufacturer, Number(value));
            } else if (fieldName.endsWith(PVSUFFIXES.inputFields.InverterModel)) {
                let modelName = this.$serviceControllers.PvSystem.getInverterModel(Number(value))?.Name;
                if (!modelName) {
                    modelName = await this.$serviceControllers.PvSystem.getPVModelName(value);
                }
                return modelName;
            } else if (fieldName.endsWith(PVSUFFIXES.inputFields.PVModuleManufacturer)) {
                return this.getManufacturerName(PVSUFFIXES.inputFields.PVModuleManufacturer, Number(value));
            } else if (fieldName.endsWith(PVSUFFIXES.inputFields.PVModuleModel)) {
                let modelName = this.$serviceControllers.PvSystem.getPvModuleModel(Number(value))?.Name;
                if (!modelName) {
                    modelName = await this.$serviceControllers.PvSystem.getPVModelName(value);
                }
                return modelName;
            } else if (fieldName.endsWith(PVSUFFIXES.inputFields.EquipmentItemModel)) {
                let modelName = this.$serviceControllers.PvSystem.getEquipmentModel(value)?.Name;
                if (!modelName) {
                    modelName = await this.$serviceControllers.PvSystem.getEquipmentModelName(value);
                }
                return modelName;
            }
        } else if (dataFieldGroupType == DATA_FIELD_GROUP_TYPE.EnergyStorageSystem && value !== null) {
            const selectedESSSubsystemType = Number(exp.PropertyDictionary[EXPRESSION_PROPERTY_NAMES.ESSSubsystemType]);
            if (
                fieldName.endsWith(ESSSUFFIXES.inputFields.ChildModel) ||
                fieldName.endsWith(ESSSUFFIXES.inputFields.SubsystemModel)
            ) {
                let modelName = this.$serviceControllers.EnergyStorageSystem.getEquipmentModel(value)?.Name;
                if (!modelName) {
                    const equipmentType =
                        selectedESSSubsystemType === ENERGY_STORAGE_SUBSYSTEM_TYPE.IntegratedEnergyStorageSystem
                            ? COMPONENT_TYPE.Integrated_Energy_Storage
                            : selectedESSSubsystemType === ENERGY_STORAGE_SUBSYSTEM_TYPE.InverterAndBattery
                              ? COMPONENT_TYPE.Storage_System_Inverter
                              : COMPONENT_TYPE.Battery;

                    modelName = await this.$serviceControllers.EnergyStorageSystem.getEquipmentModelName(
                        equipmentType,
                        value
                    );
                }
                return modelName;
            } else if (fieldName.endsWith(ESSSUFFIXES.inputFields.SubsystemType)) {
                if (Number(value) === ENERGY_STORAGE_SUBSYSTEM_TYPE.IntegratedEnergyStorageSystem) {
                    return ENERGY_STORAGE_SUBSYSTEM_NAME.IntegratedEnergyStorageSystem;
                }
                if (Number(value) === ENERGY_STORAGE_SUBSYSTEM_TYPE.InverterAndBattery) {
                    return ENERGY_STORAGE_SUBSYSTEM_NAME.InverterAndBattery;
                }
                return null;
            }
        }
        return value;
    }

    getManufacturerName(suffix, manufacturerId) {
        let manufacturers = [];
        switch (suffix) {
            case PVSUFFIXES.inputFields.InverterManufacturer:
                manufacturers = this.$serviceControllers.PvSystem.getInverterManufacturers();
                break;
            case PVSUFFIXES.inputFields.PVModuleManufacturer:
                manufacturers = this.$serviceControllers.PvSystem.getPvModuleManufacturers();
                break;
        }
        const manufacturer = manufacturers.find((x) => x.ManufacturerDefinitionId === manufacturerId);
        return manufacturer?.ListedName;
    }

    isESSSubSystemField(fieldName) {
        // This depends on the assumption that only one field name ends with each of these suffixes
        // (which isn't true in PVSUFFIXES.outputFields, so have to be mindful of this)
        const essSubSystemSuffixes = [
            ESSSUFFIXES.inputFields.SubsystemType,
            ESSSUFFIXES.inputFields.SubsystemQuantity,
            ESSSUFFIXES.inputFields.SubsystemManufacturer,
            ESSSUFFIXES.inputFields.SubsystemModel,
            ESSSUFFIXES.inputFields.SubsystemEnergyRating,
            ESSSUFFIXES.inputFields.SubsystemPowerRating,
            ESSSUFFIXES.inputFields.SubsystemTechnology
        ];
        for (const suffix in essSubSystemSuffixes) {
            if (fieldName.endsWith(essSubSystemSuffixes[suffix])) {
                return true;
            }
        }
        return false;
    }
}
